import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  ensureGuestSession,
  apiMe,
  apiUsage,
  apiRegister,
  apiLogin,
  apiGoogleSession,
  apiLogout,
  getToken,
  trackEvent,
} from "@/lib/namable";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = guest
  const [usage, setUsage] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshUsage = useCallback(async () => {
    try {
      const u = await apiUsage();
      setUsage(u);
      return u;
    } catch (e) {
      return null;
    }
  }, []);

  const loadIdentity = useCallback(async () => {
    try {
      const me = await apiMe();
      setUser(me);
    } catch (e) {
      setUser(null); // guest
    }
    await refreshUsage();
  }, [refreshUsage]);

  useEffect(() => {
    (async () => {
      try {
        if (!getToken()) await ensureGuestSession();
        await loadIdentity();
      } catch (e) {
        // still allow app to render
      } finally {
        setReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const register = useCallback(
    async (payload) => {
      const data = await apiRegister(payload);
      setUser(data.user);
      await refreshUsage();
      trackEvent("signup_completed", { method: "email" }, { user_id: data.user?.user_id });
      return data;
    },
    [refreshUsage]
  );

  const login = useCallback(
    async (payload) => {
      const data = await apiLogin(payload);
      setUser(data.user);
      await refreshUsage();
      trackEvent("login_completed", { method: "email" }, { user_id: data.user?.user_id });
      return data;
    },
    [refreshUsage]
  );

  const googleLogin = useCallback(
    async (sessionId) => {
      const data = await apiGoogleSession(sessionId);
      setUser(data.user);
      await refreshUsage();
      trackEvent("login_completed", { method: "google" }, { user_id: data.user?.user_id });
      return data;
    },
    [refreshUsage]
  );

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    await ensureGuestSession();
    await refreshUsage();
  }, [refreshUsage]);

  const track = useCallback(
    (event, props = {}) =>
      trackEvent(event, props, { user_id: user?.user_id, plan: usage?.plan }),
    [user, usage]
  );

  const value = {
    user,
    usage,
    ready,
    isGuest: !user,
    refreshUsage,
    setUser,
    register,
    login,
    googleLogin,
    logout,
    track,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
